import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowLeft, Loader2, Shield, KeyRound } from 'lucide-react';
import CaptchaWidget from '@/components/CaptchaWidget';

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const { login, verifyTOTP } = useAuth();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ email: '', password: '' });

  // 2FA state
  const [twoFAStep, setTwoFAStep] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [totpCode, setTotpCode] = useState('');

  // CAPTCHA state
  const [captchaConfig, setCaptchaConfig] = useState(null);
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');

  const captchaRequired = captchaConfig?.captcha_enabled && captchaConfig?.admin_login_captcha;
  const captchaType = captchaConfig?.captcha_type || 'math';
  const captchaPassed = !captchaRequired || (captchaType === 'math' ? captchaVerified : !!captchaToken);

  useEffect(() => {
    axios.get(`${API}/auth/captcha-config`)
      .then(r => setCaptchaConfig(r.data))
      .catch(() => {});
  }, []);

  const handleCaptchaVerified = useCallback((v) => setCaptchaVerified(v), []);
  const handleCaptchaToken = useCallback((t) => setCaptchaToken(t), []);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!captchaPassed) {
      toast.error('Please complete the CAPTCHA verification');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        ...(captchaRequired && captchaType !== 'math' ? { captcha_token: captchaToken } : {}),
      };
      const result = await login(payload.email, payload.password, payload.captcha_token);
      // 2FA required — show TOTP step
      if (result?.requires_2fa) {
        setTempToken(result.temp_token);
        setTwoFAStep(true);
        return;
      }
      if (result.role !== 'admin') {
        toast.error('Access denied. Admin privileges required.');
        navigate('/dashboard');
        return;
      }
      toast.success('Admin login successful!');
      navigate('/admin');
    } catch (error) {
      const detail = error.response?.data?.detail || '';
      if (detail === 'CAPTCHA_FAILED') {
        toast.error('CAPTCHA verification failed. Please try again.');
      } else {
        toast.error(detail || 'Login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    if (!totpCode || totpCode.length !== 6) {
      toast.error('Please enter the 6-digit code');
      return;
    }
    setLoading(true);
    try {
      const userData = await verifyTOTP(tempToken, totpCode);
      if (userData.role !== 'admin') {
        toast.error('Access denied. Admin privileges required.');
        navigate('/dashboard');
        return;
      }
      toast.success('Admin login successful!');
      navigate('/admin');
    } catch (error) {
      const detail = error.response?.data?.detail || 'Invalid code. Please try again.';
      toast.error(detail);
      setTotpCode('');
    } finally {
      setLoading(false);
    }
  };

  // ── 2FA step ──────────────────────────────────────────────────────────────
  if (twoFAStep) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="admin-2fa-page">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-destructive/10 via-transparent to-transparent" />
        <div className="w-full max-w-md relative z-10">
          <div className="flex items-center gap-2 mb-8">
            <div className="w-10 h-10 rounded-lg bg-destructive/80 flex items-center justify-center">
              <KeyRound className="h-5 w-5 text-white" />
            </div>
            <span className="font-bold text-2xl">Two-Factor Auth</span>
          </div>
          <Card className="border-border bg-card">
            <CardHeader>
              <CardTitle>Enter Verification Code</CardTitle>
              <CardDescription>Open your authenticator app and enter the 6-digit code.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handle2FASubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="totp-code">Authentication Code</Label>
                  <Input
                    id="totp-code"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="000000"
                    value={totpCode}
                    onChange={e => setTotpCode(e.target.value.replace(/\D/g, ''))}
                    className="text-center text-2xl tracking-[0.5em] font-mono"
                    autoFocus
                    data-testid="admin-totp-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-destructive/80 hover:bg-destructive text-white"
                  disabled={loading || totpCode.length !== 6}
                  data-testid="admin-totp-submit-btn"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Verify & Sign In'}
                </Button>
                <button
                  type="button"
                  onClick={() => { setTwoFAStep(false); setTotpCode(''); setTempToken(''); }}
                  className="w-full text-sm text-muted-foreground hover:text-foreground text-center"
                >
                  Back to login
                </button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ── Login step ────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="admin-login-page">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-destructive/10 via-transparent to-transparent"></div>
      
      <div className="w-full max-w-md relative z-10">
        <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 transition-colors">
          <ArrowLeft className="h-4 w-4" />
          Back to Home
        </Link>
        
        <div className="flex items-center gap-2 mb-8">
          <div className="w-10 h-10 rounded-lg bg-destructive/80 flex items-center justify-center">
            <Shield className="h-5 w-5 text-white" />
          </div>
          <span className="font-bold text-2xl">Admin Portal</span>
        </div>

        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle className="">Admin Access</CardTitle>
            <CardDescription>Sign in to the administration panel</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="admin-email">Email</Label>
                <Input
                  id="admin-email"
                  type="email"
                  placeholder="admin@coinpayup.com"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  data-testid="admin-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-password">Password</Label>
                <Input
                  id="admin-password"
                  type="password"
                  placeholder="••••••••"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  required
                  data-testid="admin-password-input"
                />
              </div>

              {/* CAPTCHA */}
              {captchaRequired && (
                <CaptchaWidget
                  type={captchaType}
                  siteKey={captchaConfig?.[`${captchaType}_site_key`] || ''}
                  onVerified={handleCaptchaVerified}
                  onToken={handleCaptchaToken}
                />
              )}

              <Button 
                type="submit" 
                className="w-full bg-destructive/80 hover:bg-destructive text-white"
                disabled={loading || !captchaPassed}
                data-testid="admin-login-submit-btn"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Access Admin Panel'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminLoginPage;

